import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import GoogleSignInButton from './GoogleSignInButton';

// Email/password authentication. The backend issues working tokens on
// register/login without requiring email verification (the verification email
// is fire-and-forget and degrades cleanly if SMTP is unset), so this is safe
// to expose. Overridable via env for staged rollout.
const ENABLE_EMAIL_AUTH = process.env.REACT_APP_ENABLE_EMAIL_AUTH !== 'false';

// PageLayout moved OUTSIDE the component to prevent re-creation on every render
const PageLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header with Logo */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity w-fit">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                        <span className="text-white font-bold text-xl">B</span>
                    </div>
                    <span className="text-2xl font-bold text-content-primary">
                        BlockNotify
                    </span>
                </Link>
            </div>
        </header>

        {/* Main Content */}
        <div className="py-12 px-4 sm:px-6 lg:px-8">
            {children}
        </div>
    </div>
);

const EnhancedLoginForm = () => {
    // Email/password form state
    const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState(null);

    const { isAuthenticated, loading: authLoading, login, register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const getPostLoginPath = useCallback(() => {
        const next = searchParams.get('next');
        if (next && next.startsWith('/') && !next.startsWith('//')) {
            return next;
        }
        // After signing in, land on the user's own dashboard — not the public
        // market feed. Guests explore /explore; a signed-in user wants their
        // watchlist, alerts, and activity. Same destination on mobile.
        return '/dashboard';
    }, [searchParams]);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate(getPostLoginPath());
        }
    }, [isAuthenticated, navigate, getPostLoginPath]);

    // Show loading state while checking auth status OR if already authenticated (redirect pending)
    // This prevents clicking login buttons while auth is being verified or redirect is in progress
    if (authLoading || isAuthenticated) {
        return (
            <PageLayout>
                <div className="w-full max-w-md mx-auto">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h2 className="text-base font-bold text-content-primary mb-2">
                            {isAuthenticated ? 'Redirecting...' : 'Checking authentication...'}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                            {isAuthenticated ? 'You are already signed in' : 'Please wait while we verify your session'}
                        </p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setEmailLoading(true);
        setEmailError(null);

        try {
            let result;
            if (authMode === 'register') {
                result = await register({ email, password });
                if (!result.success) {
                    throw new Error(result.error || 'Registration failed');
                }
            } else {
                result = await login(email, password);
                if (!result.success) {
                    throw new Error(result.error || 'Login failed');
                }
            }
            navigate(getPostLoginPath());
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Authentication failed';
            setEmailError(errorMsg);
        } finally {
            setEmailLoading(false);
        }
    };

    return (
        <PageLayout>
            <div className="w-full max-w-md mx-auto">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-content-primary mb-2">
                        {ENABLE_EMAIL_AUTH && authMode === 'register' ? 'Create your account' : 'Welcome back'}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        {ENABLE_EMAIL_AUTH && authMode === 'register' ? 'Start monitoring your wallets today' : 'Sign in to access your alerts'}
                    </p>
                </div>

                {/* Email/Password Form - Hidden behind feature flag until SMTP is configured */}
                {ENABLE_EMAIL_AUTH && (
                    <>
                        <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-content-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-content-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                />
                            </div>
                            
                            {emailError && (
                                <p className="text-red-500 text-sm">{emailError}</p>
                            )}

                            <button
                                type="submit"
                                disabled={emailLoading}
                                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {emailLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {authMode === 'register' ? 'Creating account...' : 'Signing in...'}
                                    </span>
                                ) : (
                                    authMode === 'register' ? 'Create Account' : 'Sign In'
                                )}
                            </button>
                        </form>

                        {/* Toggle Login/Register */}
                        <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                            {authMode === 'register' ? (
                                <>
                                    Already have an account?{' '}
                                    <button
                                        onClick={() => { setAuthMode('login'); setEmailError(null); }}
                                        className="text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        Sign in
                                    </button>
                                </>
                            ) : (
                                <>
                                    Don't have an account?{' '}
                                    <button
                                        onClick={() => { setAuthMode('register'); setEmailError(null); }}
                                        className="text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        Sign up free
                                    </button>
                                </>
                            )}
                        </p>

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500">Or continue with</span>
                            </div>
                        </div>
                    </>
                )}

                {/* Google Sign-In */}
                <div className="mb-3">
                    <GoogleSignInButton />
                </div>

            </div>
        </PageLayout>
    );
};

export default EnhancedLoginForm;

